<template>
	<div class="appGrey" style="min-height: 100%">

		{{ computedInit }}

		<!--Supervisors -------------------------------------------------------------------------------------------- -->
		<div>

			<!--Label-->
			<app-text size="medium-bold">Supervisors</app-text>

			<!--Form-->
			<div class="d-flex align-center justify-space-between mt-4">

				<!--Supervisors Selection-->
				<app-form-field form-type="autoComplete"
								:disabled="!isSupervisorsEditable"
								:items="computedSupervisorsDropDownData"
								item-text="userName"
								label="Available Supervisors"
								:multiple="true"
								:return-object="true"
								:small-chips="true"
								style="width: 100%"
								v-model="tempTeamSupervisors"/>

				<!--Save Button-->
				<app-btn v-if="isSupervisorsEditable"
						 @click.native="saveSupervisors(tempTeamSupervisors)"
						 class="ml-4"
						 color="green"
						 :disabled="!tempTeamSupervisors.length"
						 icon="save"
						 label="Save"/>

				<!--Edit Button-->
				<edit-icon @click.native="editSupervisor" :isActive="isSupervisorsEditable"/>

			</div>

			<!--Table-->
			<v-data-table
				class="appWhite rounded-lg mt-4"
				:items="teamSupervisors"
				:headers="tableHeaders">

				<!--Supervisor-->
				<template v-slot:item.userName="{item}">
					<app-text size="small">{{ item.userName }}</app-text>
				</template>

				<!--Delete-->
				<template v-slot:item.delete="{item}">
					<app-icon @click.native="removeSupervisor(item)" class="cursorPointer pa-2" color="red"
							  icon="delete"/>
				</template>

			</v-data-table>

		</div>

		<!--Deputies ----------------------------------------------------------------------------------------------- -->
		<div class="mt-4">

			<!--Label-->
			<app-text size="medium-bold">Deputies</app-text>

			<!--Form-->
			<div class="d-flex align-center justify-space-between mt-4">

				<!--Deputies-->
				<app-form-field form-type="autoComplete"
								:disabled="!isDeputiesEditable"
								:items="computedDeputiesDropDownData"
								item-text="userName"
								label="Available Deputies"
								:multiple="true"
								:return-object="true"
								:small-chips="true"
								style="width: 100%"
								v-model="tempTeamDeputies"/>

				<!--Save Button-->
				<app-btn v-if="isDeputiesEditable"
						 @click.native="saveDeputies(tempTeamDeputies)"
						 class="ml-4"
						 color="green"
						 :disabled="!tempTeamDeputies.length"
						 icon="save"
						 label="Save"/>

				<!--Edit Button-->
				<edit-icon @click.native="editDeputies" :isActive="isDeputiesEditable"/>

			</div>

			<!--Table-->
			<v-data-table
				class="appWhite rounded-lg mt-4"
				:items="teamDeputies"
				:headers="tableHeaders">

				<!--Deputy-->
				<template v-slot:item.userName="{item}">
					<app-text size="small">{{ item.userName }}</app-text>
				</template>

				<!--Delete-->
				<template v-slot:item.delete="{item}">
					<app-icon @click.native="removeDeputy(item)" class="cursorPointer pa-2" color="red" icon="delete"/>
				</template>

			</v-data-table>

		</div>

	</div>
</template>

<script>
export default {

	name: "TeamSupervisorForm",

	props: ['formData', 'isReadOnly', 'usersData'],

	data: () => ({
		tableHeaders: [
			{text: 'Name', value: 'userName', sortable: false, align: 'start'},
			{text: 'Actions', value: 'delete', sortable: false, align: 'center', width: '10px'},
		],

		isSupervisorsEditable: false,
		tempTeamSupervisors: [],
		teamSupervisors: [],

		isDeputiesEditable: false,
		tempTeamDeputies: [],
		teamDeputies: [],
	}),

	computed: {

		/**
		 * Computed Init
		 *
		 * If there is any form data, assign it to the form.
		 */
		computedInit() {
			const t = this
			const FORM_DATA = t.$props?.formData?.teamData

			if (FORM_DATA?.entityId) {

				// Assign all the users for this team
				// Supervisors and Deputies, or, Chiefs and Deputies if they are part of the Steward Management team
				t.teamSupervisors = t.$props.usersData.filter(u => (u.userLevel === 'Steward-Supervisor' || u.userPayGrade === 'Chief Steward') && u.userTeams.includes(FORM_DATA.entityId))
				t.teamDeputies = t.$props.usersData.filter(u => (u.userLevel === 'Steward-Deputy' || u.userPayGrade === 'Deputy Chief Steward') && u.userTeams.includes(FORM_DATA.entityId))

				// Sort the users by name
				t.teamSupervisors.sort((a, b) => a.userName > b.userName ? 1 : -1)
				t.teamDeputies.sort((a, b) => a.userName > b.userName ? 1 : -1)
			}

		},

		/**
		 * Supervisors Drop Down Data
		 *
		 * Return an array of Supervisors that are not already assigned to this team.
		 *
		 * @returns {array} array of Supervisors
		 */
		computedSupervisorsDropDownData() {
			const t = this
			const USERS_DATA = t.$props.usersData

			// Get all the Supervisors and Chiefs that are not already assigned to this team.
			const SUPERVISORS = USERS_DATA.filter(u => (u.userLevel === 'Steward-Supervisor' || u.userPayGrade === 'Chief Steward') && !u.userTeams.includes(t.$props.formData.teamData.entityId))

			return SUPERVISORS?.sort((a, b) => a.userName > b.userName ? 1 : -1)
		},

		/**
		 * Deputies Drop Down Data
		 *
		 * Return an array of Deputies that are not already assigned to this team.
		 *
		 * @returns {array} array of Deputies
		 */
		computedDeputiesDropDownData() {
			const t = this
			const USERS_DATA = t.$props.usersData

			// Get all the Deputies and Deputy Chiefs that are not already assigned to this team.
			const DEPUTIES = USERS_DATA.filter(u => (u.userLevel === 'Steward-Deputy' || u.userPayGrade === 'Deputy Chief Steward') && !u.userTeams.includes(t.$props.formData.teamData.entityId))

			return DEPUTIES?.sort((a, b) => a.userName > b.userName ? 1 : -1)
		},

	},

	methods: {

		/**
		 * Edit Supervisor
		 *
		 * Toggle the editability.
		 */
		editSupervisor() {
			const t = this

			t.isSupervisorsEditable = !t.isSupervisorsEditable
		},

		/**
		 * Edit Deputy
		 *
		 * Toggle the editability.
		 */
		editDeputies() {
			const t = this

			t.isDeputiesEditable = !t.isDeputiesEditable
		},

		/**
		 * Reload Data
		 *
		 * Emit back to the parent (Teams.vue) to reload the data to update the page.
		 */
		reloadData() {
			const t = this

			t.$emit('emitReloadData')
		},

		/**
		 * Remove Deputy
		 *
		 * Remove the Deputy from the current Team by updating their userTeams array.
		 *
		 * @param userData the user object to remove
		 * @returns {Promise<void>}
		 */
		async removeDeputy(userData) {
			const t = this

			// Remove the current team from the userTeams array
			userData.userTeams = userData.userTeams.filter(ut => ut !== t.$props.formData.teamData.entityId)

			const RESPONSE = await t.MIX_redis_update('user', userData.entityId, userData)

			// Handle any errors
			if (RESPONSE.hasErrors) {
				console.error('Error updating User: ', RESPONSE.error)
				return
			}

			// Remove the user from the tempTeamDeputies array to stop them showing up in the Available Deputies
			// selection field
			t.tempTeamDeputies = t.tempTeamDeputies.filter(ts => ts.entityId !== userData.entityId)

			t.$sharedState.successMessage = 'Removed Deputy'
		},

		/**
		 * Remove Supervisor
		 *
		 * Remove the Supervisor from the current Team by updating their userTeams array.
		 *
		 * @param userData the user object to remove
		 * @returns {Promise<void>}
		 */
		async removeSupervisor(userData) {
			const t = this

			// Remove the current team from the userTeams array
			userData.userTeams = userData.userTeams.filter(ut => ut !== t.$props.formData.teamData.entityId)

			const RESPONSE = await t.MIX_redis_update('user', userData.entityId, userData)

			// Handle any errors
			if (RESPONSE.hasErrors) {
				console.error('Error updating User: ', RESPONSE.error)
				return
			}

			// Remove the user from the tempTeamSupervisors array to stop them showing up in the Available Supervisors
			// selection field
			t.tempTeamSupervisors = t.tempTeamSupervisors.filter(ts => ts.entityId !== userData.entityId)

			t.$sharedState.successMessage = 'Removed Supervisor'
		},

		/**
		 * Save Deputies
		 *
		 * Send the new teamDeputies array off to the server to update the list of Deputies on this team.
		 */
		async saveDeputies(teamDeputies) {
			const t = this
			const FORM_DATA = t.$props?.formData?.teamData

			// If there are no deputies, return
			if (!t.tempTeamDeputies.length) return

			// Update Users
			const RESPONSE = await t.MIX_redis_updateUsersTeam(teamDeputies, FORM_DATA.entityId)

			// Handle any errors
			if (RESPONSE.hasErrors) {
				console.error('Error updating User: ', RESPONSE.error)
				return
			}

			t.$sharedState.successMessage = 'Updated Deputies'

			t.isDeputiesEditable = false

			t.reloadData()
		},

		/**
		 * Save Supervisors
		 *
		 * Send the new teamSupervisors array off to the server to update the list of Supervisors on this team.
		 */
		async saveSupervisors(teamSupervisors) {
			const t = this
			const FORM_DATA = t.$props?.formData?.teamData

			// If there are no supervisors, return
			if (!t.tempTeamSupervisors.length) return

			// Update Users
			const RESPONSE = await t.MIX_redis_updateUsersTeam(teamSupervisors, FORM_DATA.entityId)

			// Handle any errors
			if (RESPONSE.hasErrors) {
				console.error('Error updating User: ', RESPONSE.error)
				return
			}

			t.$sharedState.successMessage = 'Updated Supervisors'

			t.isSupervisorsEditable = false

			t.reloadData()
		},

	}

}
</script>

<style scoped>

</style>
